/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';

import TextField from '../../components/OpmsForm/Fields/TextField';

import Select from '../../components/OpmsForm/Fields/Select';


import { InputAdornment, Paper, styled } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import Checkbox from '../../components/OpmsForm/Fields/Checkbox';
import DatePicker from '../../components/OpmsForm/Fields/DatePicker';
import NumericField, { FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE } from '../../components/OpmsForm/Fields/NumericField';
import OpmsStaticGrid, { IOpmsStaticGridHeader } from '../../components/OpmsForm/Grids/Static/OpmsStaticGrid';
import OpmsStaticRow, { IOpmsStaticRow } from '../../components/OpmsForm/Grids/Static/OpmsStaticRow';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import OpmsFieldGroup from '../../components/OpmsForm/OpmsFieldGroup';
import OpmsFieldLabel from '../../components/OpmsForm/OpmsFieldLabel';
import SourceDocuments from '../../components/OpmsForm/SourceDocuments';
import TrialForm from '../../components/OpmsForm/TrialForm';
import { GetFieldLookupItem } from '../../context/LookupsContext';
import { FormContext, PatientContext } from '@ngt/online-patient-management-ui';
import PageLayout from 'src/components/Layout/PageLayout';
import theme from 'src/styles/theme';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDiagnosticImagingAndTumourAssessmentProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    subHeading: {
        padding: theme.spacing(3)
    },
    instructions: {
        padding: theme.spacing(3)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const doesNotHaveClinicalExaminationNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.clinicalExaminationNotDone;
const doesNotHaveCytologicalAssessmentNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.cytologicalAssessmentNotDone;
const hasCytologicalAssessmentRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => GetFieldLookupItem(lookups, 'CytologicalAssessmentId', formState.values.cytologicalAssessmentId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const doesNotHaveAbdomenUltrasoundNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.abdomenUltrasoundNotDone;
const doesNotHaveAbdomenUltrasoundNotApplicable = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.abdomenUltrasoundNotApplicable;
const doesNotHaveAbdomenUltrasoundNotDoneOrApplicable = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.abdomenUltrasoundNotDone && !formState.values.abdomenUltrasoundNotApplicable;
const hasAbdomenUltrasoundRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => GetFieldLookupItem(lookups, 'AbdomenUltrasoundId', formState.values.abdomenUltrasoundId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const doesNotHaveChestXrayNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.chestXrayNotDone;
const hasChestXrayRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => GetFieldLookupItem(lookups, 'ChestXrayId', formState.values.chestXrayId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const doesNotHaveCtScanNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.ctScanNotDone;
const hasCtScanRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => GetFieldLookupItem(lookups, 'CtScanId', formState.values.ctScanId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const doesNotHavePetScanNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.petScanNotDone;
const hasPetScanRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => GetFieldLookupItem(lookups, 'PetScanId', formState.values.petScanId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const doesNotHaveBoneScanNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.boneScanNotDone;
const hasBoneScanRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => GetFieldLookupItem(lookups, 'BoneScanId', formState.values.boneScanId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const mammogramTypeBilateral = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => formState.values.mammogramTypeId === Dtos.LookupMammogramType.Bilateral as number;
const mammogramTypeUnilateral = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => formState.values.mammogramTypeId === Dtos.LookupMammogramType.Unilateral as number;
const doesNotHaveAxillaClipInsertedNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.axillaClipInsertedNotDone;
const doesNotHaveAxillaUltrasoundNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.axillaUltrasoundNotDone;
const doesNotHaveMriIncludingAxillaNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticImagingAndTumourAssessment>) => !formState.values.mriIncludingAxillaNotDone;

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const breastUltrasoundHeader: IOpmsStaticGridHeader[] = [
    {
        columnName: 'lesion',
        content: '',
        width: '15%',
        minWidth: 150
    },
    {
        columnName: 'side',
        content: 'Side',
        minWidth: 150
    },
    {
        columnName: 'location',
        content: 'Location (o\'clock)',
        width: '15%',
        minWidth: 150
    },
    {
        columnName: 'dimensionOne',
        content: 'Dimension One',
        width: '25%',
        minWidth: 150
    },
    {
        columnName: 'dimensionTwo',
        content: 'Dimension Two',
        width: '25%',
        minWidth: 150
    }
];

const breastUltrasoundRow: Array<IOpmsStaticRow<Dtos.DiagnosticImagingAndTumourAssessment>> = [
    {
        columnName: 'lesion',
        content: (
            <Typography>
                <strong>
                    Breast Lesion
                </strong>
            </Typography>
        ),
        fieldName: 'rowName'
    },
    {
        columnName: 'side',
        fieldName: 'BreastUltrasoundLesionSideId',
        content: (
            <OpmsInput
                component={Select}
            />
        )
    },
    {
        columnName: 'location',
        fieldName: 'BreastUltrasoundLesionLocationId',
        content: (
            <OpmsInput
                component={Select}
            />
        )
    },
    {
        columnName: 'dimensionOne',
        fieldName: 'BreastUltrasoundLesionDimensionOne',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mm&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        )
    },
    {
        columnName: 'dimensionTwo',
        fieldName: 'BreastUltrasoundLesionDimensionTwo',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            mm&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        )
    }
];

//const mammogramHeader: IOpmsStaticGridHeader[] = [
//    {
//        columnName: 'lesion',
//        content: '',
//        width: '40%',
//        minWidth: 150
//    },
//    {
//        columnName: 'side',
//        content: 'Side',
//        width: '30%',
//        minWidth: 150
//    },
//    {
//        columnName: 'location',
//        content: 'Location',
//        width: '30%',
//        minWidth: 150
//    },
//    //{
//    //    columnName: 'dimension',
//    //    content: 'Dimension',
//    //    width: '50%',
//    //    minWidth: 150
//    //}
//];

//const mammogramRow: Array<IOpmsStaticRow<Dtos.DiagnosticImagingAndTumourAssessment>> = [
//    {
//        columnName: 'lesion',
//        content: (
//            <Typography>
//                <strong>
//                    Breast Lesion
//                </strong>
//            </Typography>
//        ),
//        fieldName: 'rowName'
//    },
//    {
//        columnName: 'side',
//        fieldName: 'MammogramLesionSideId',
//        content: (
//            <OpmsInput
//                component={Select}
//            />
//        )
//    },
//    {
//        columnName: 'location',
//        fieldName: 'MammogramLesionLocationId',
//        content: (
//            <OpmsInput
//                component={Select}
//            />
//        )
//    },
//    //{
//    //    columnName: 'dimension',
//    //    fieldName: 'MammogramLesionDimension',
//    //    content: (
//    //        <OpmsInput
//    //            component={NumericField}
//    //            InputProps={{
//    //                endAdornment: (
//    //                    <InputAdornment position="end">
//    //                        mm&nbsp;
//    //                    </InputAdornment>
//    //                )
//    //            }}
//    //            numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
//    //        />
//    //    )
//    //}
//];

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const Underlined = styled('span') (() => ({
    textDecoration: 'underline'
}))

const DiagnosticImagingAndTumourAssessment: React.FunctionComponent<IDiagnosticImagingAndTumourAssessmentProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    return (
        //@ts-ignore
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="Diagnostic Imaging and Tumour Assessment"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                >
                    <Typography
                        className={classes.instructions}
                    >
                        <strong>Only report diagnostic imaging information on this page.</strong> The U/S report should include bidimensional measurements of
                        the longest two perpendicular diameters of the breast tumour and {patientCohortC ? 'include' : 'the ultrasound should also report' } an assessment of
                        the axilla*. The measurement should include the greatest extent of disease including intervening area of non-enhancing
                        tissue. Multifocal disease is an exclusion criteria. Patients with <em>in situ</em> disease in addition to the primary breast
                        carcinoma are eligible. <em>in situ</em> disease is not required to be reported.  In the event of abnormal imaging of the axilla,
                        nodal status should be confirmed by cytology. Clinically node positive patients should undergo CT chest or PET CT chest
                        / abdomen (and bone scan if indicated) to exclude metastases. Abdominal ultrasound, CT or PET CT scan (chest, abdomen)
                        is required within 35 days prior to { patientCohortC ? 'registration' : 'randomisation' } if alkaline phosphatase or AST is &gt; 2.5 &times; ULN.
                        <br />
                        <br />
                        * Baseline axilla ultrasound is not required when an MRI including the axilla has been performed. <Underlined>Where axilla was assessed by MRI at baseline it 
                        will need to be assessed by MRI at the pre-surgery visit.</Underlined>
                    </Typography>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Breast
                        </Typography>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Ultrasound
                        </Typography>
                        <OpmsField name="BreastUltrasoundDate">
                            <OpmsInput
                                component={DatePicker}
                            />
                        </OpmsField>
                        <OpmsStaticGrid
                            headers={breastUltrasoundHeader}
                        >
                            <OpmsStaticRow
                                cells={breastUltrasoundRow}
                            />
                        </OpmsStaticGrid>
                        <OpmsField
                            name="BreastClipInsertedDate"
                        >
                            <OpmsInput
                                component={DatePicker}
                            />
                        </OpmsField>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Axilla
                        </Typography>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Ultrasound
                        </Typography>
                        <OpmsField
                            name="AxillaUltrasoundNotDone"
                            label={<OpmsFieldLabel name={"AxillaUltrasoundDate"} />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveAxillaUltrasoundNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="AxillaUltrasoundDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField
                                name="AxillaUltrasoundNumberOfSuspiciousAxillaryNodes"
                            >
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            MRI (Including Axilla)
                        </Typography>
                        <OpmsField
                            name="MriIncludingAxillaNotDone"
                            label={<OpmsFieldLabel name="MriIncludingAxillaDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveMriIncludingAxillaNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="MriIncludingAxillaDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField
                                name="MriSuspiciousAxillaryNodes"
                                label={<OpmsFieldLabel name="MriSuspiciousAxillaryNodes" />}
                            >
                                <OpmsInput
                                    component={NumericField}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Clip Insertion (Axilla)
                        </Typography>
                        <OpmsField
                            name="AxillaClipInsertedNotDone"
                            label={<OpmsFieldLabel name="AxillaClipInsertedDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveAxillaClipInsertedNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="AxillaClipInsertedDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Clinical Examination
                        </Typography>
                        <OpmsField
                            name="ClinicalExaminationNotDone"
                            label={<OpmsFieldLabel name="ClinicalExaminationDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveClinicalExaminationNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField name="ClinicalExaminationDate" label={null}>
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="ClinicalExaminationNumberOfSuspiciousAxillaryNodes">
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Cytological Assessment
                        </Typography>
                        <OpmsField
                            name="CytologicalAssessmentNotDone"
                            label={<OpmsFieldLabel name="CytologicalAssessmentDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveCytologicalAssessmentNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="CytologicalAssessmentDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="CytologicalAssessmentId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                                condition={hasCytologicalAssessmentRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="CytologicalAssessmentSpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                        </OpmsConditional>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Abdomen
                        </Typography>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveAbdomenUltrasoundNotApplicable}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="AbdomenUltrasoundNotDone"
                                label={<OpmsFieldLabel name="AbdomenUltrasoundDate" />}
                            >
                                <OpmsInput
                                    component={Checkbox}
                                    label="Not Done"
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveAbdomenUltrasoundNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="AbdomenUltrasoundNotApplicable"
                                label={null}
                            >
                                <OpmsInput
                                    component={Checkbox}
                                    label="Not Applicable"
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveAbdomenUltrasoundNotDoneOrApplicable}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="AbdomenUltrasoundDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="AbdomenUltrasoundId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                                condition={hasAbdomenUltrasoundRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="AbdomenUltrasoundSpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                        </OpmsConditional>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Mammogram
                        </Typography>
                        <Typography
                            className={classes.instructions}
                        >
                            <strong>Only report diagnostic imaging information on this page.</strong> A bilateral mammogram is required within 6 weeks of {patientCohortC ? 'registration' : 'randomisation' }. Multifocal disease is an exclusion criteria for the study.
                        </Typography>
                        <OpmsField
                            name="MammogramTypeId"
                        >
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={mammogramTypeBilateral}
                            mode="Show"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="MammogramDate"
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={mammogramTypeUnilateral}
                            mode="Show"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="MammogramLeftDate"
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField
                                name="MammogramRightDate"
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        {
                            //<OpmsStaticGrid
                            //    headers={mammogramHeader}
                            //>
                            //    <OpmsStaticRow
                            //        cells={mammogramRow}
                            //    />
                            //</OpmsStaticGrid>
                        }
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Chest X-ray
                        </Typography>
                        <Typography
                            className={classes.instructions}
                        >
                            <strong>Only report diagnostic imaging information on this page.</strong> A chest x-ray (or CT or PET) is required within
                            35 days of {patientCohortC ? 'registration' : 'randomisation' }.
                        </Typography>
                        <OpmsField
                            name="ChestXrayNotDone"
                            label={<OpmsFieldLabel name="ChestXrayDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveChestXrayNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="ChestXrayDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="ChestXrayId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                                condition={hasChestXrayRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="ChestXraySpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                        </OpmsConditional>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Other
                        </Typography>
                        <Typography
                            className={classes.instructions}
                        >
                            Participants who have undergone an abdominal and chest PET or CT scan do not require a chest X-ray. A Bone scan is required if clinically indicated, if alkaline phosphatase is &gt; 2.5 x ULN or if the
                            participant has unexplained bone pain, and the patient has not had a PET scan.
                        </Typography>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            CT Scan
                        </Typography>
                        <OpmsField
                            name="CtScanNotDone"
                            label={<OpmsFieldLabel name="CtScanDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveCtScanNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="CtScanDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="CtScanId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                                condition={hasCtScanRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="CtScanSpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                        </OpmsConditional>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            PET Scan
                        </Typography>
                        <OpmsField
                            name="PetScanNotDone"
                            label={<OpmsFieldLabel name="PetScanDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHavePetScanNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="PetScanDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="PetScanId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                                condition={hasPetScanRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="PetScanSpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                        </OpmsConditional>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Bone Scan
                        </Typography>
                        <OpmsField
                            name="BoneScanNotDone"
                            label={<OpmsFieldLabel name="BoneScanDate" />}
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                            condition={doesNotHaveBoneScanNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="BoneScanDate"
                                label={null}
                            >
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                            <OpmsField name="BoneScanId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticImagingAndTumourAssessment}
                                condition={hasBoneScanRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="BoneScanSpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                        </OpmsConditional>
                    </OpmsFieldGroup>
                    <SourceDocuments />
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default DiagnosticImagingAndTumourAssessment;